import React, { ReactNode, useEffect, useLayoutEffect, useRef } from 'react';
import './ProcessTimeline.scss';

import 'PDF/components/Timeline/TimelineComponent.scss';
import { ReactComponent as ProcessStagesBackground } from 'PDF/assets/images/ProcessStagesBackground.svg';

import { ReactComponent as ProcessStagesIcon } from './ProcessStagesIcon.svg';

interface ITimelineProps {
  className?: string;
  tip?: boolean;
  children: ReactNode;
}

export const ProcessTimeline: React.FC<ITimelineProps> = ({ className, tip = true, children }) => {
  const tipClassName = tip ? 'with-tip' : '';
  const fullClassName = `container process process-timeline-wrapper ${tipClassName} ${className || ''}`;
  const numBookmarks = React.Children.count(children);
  const timelineRef = useRef<HTMLDivElement>(null);
  const styleRef = useRef<HTMLStyleElement | null>(null);

  useLayoutEffect(() => {
    if (!styleRef.current) {
      styleRef.current = document.createElement('style');
      styleRef.current.setAttribute('data-timeline-styles', '');
    }

    const cssRules = Array.from(
      { length: numBookmarks },
      (_, i) => `.timeline-item:nth-child(${i + 1}) { grid-row: ${i + 1}; }`,
    ).join('\n');

    styleRef.current.textContent = cssRules;

    if (!styleRef.current.parentNode) {
      document.head.appendChild(styleRef.current);
    }

    return () => {
      if (styleRef.current?.parentNode) {
        styleRef.current.parentNode.removeChild(styleRef.current);
      }
      styleRef.current = null;
    };
  }, [numBookmarks]);

  useEffect(() => {
    const dotmarks = timelineRef.current?.querySelectorAll('.timeline-dotmark');
    if (dotmarks) {
      const lastDotmark = dotmarks[dotmarks.length - 1];
      lastDotmark?.querySelector('.timeline-dot')?.classList.add('no-after');
    }
  }, [children]);

  return (
    <div className={fullClassName} ref={timelineRef}>
      <div className="process-background-image">
        <ProcessStagesBackground />
      </div>
      <div className="timeline-connector" />
      <ul className="timeline">
        {children}
        {Array(numBookmarks)
          .fill(null)
          .map((_, i) => (
            <span key={i} className="timeline-dotmark">
              <div
                className={`timeline-dot ${i === numBookmarks - 1 ? ((i + 1) % 2 == 1 ? 'last-timeline-dot-odd' : 'last-timeline-dot-even') : ''}`}
              >
                <ProcessStagesIcon />
              </div>
            </span>
          ))}
      </ul>
    </div>
  );
};
